import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { CtaButton } from '../../../../../types';

interface CtaButtonCardProps {
  ctaButton: CtaButton;
  onEdit: (ctaButton: CtaButton) => void;
  onDelete: (id: string) => void;
}

export const CtaButtonCard: React.FC<CtaButtonCardProps> = ({
  ctaButton,
  onEdit,
  onDelete
}) => {
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: '1px solid #E0E0E0',
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        boxShadow: 'none',
        width: '320px',
        height: '360px',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2, position: 'relative' }}>
        {/* 右上の設定アイコン */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(ctaButton);
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#666666',
          }}
        >
          <SettingsIcon />
        </IconButton>

        {/* カードタイトル */}
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            fontWeight: 600, 
            fontSize: '16px',
            mb: 2,
            pr: 4,
          }}
        >
          {ctaButton.name}
        </Typography>

        {/* CTAボタン画像プレビュー */}
        {ctaButton.image && (
          <Box
            sx={{
              width: '100%',
              height: '120px',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F5F5F5',
              borderRadius: '10px',
            }}
          >
            <img
              src={ctaButton.image}
              alt={ctaButton.name}
              style={{
                maxWidth: '100%',
                maxHeight: '120px',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        {/* 設定情報 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* 固定表示タグ */}
          <Chip 
            label={ctaButton.displayMode === 'embedded' ? '埋め込み式' : '固定表示'}
            size="small"
            sx={{ 
              fontSize: '12px',
              height: '24px',
              width: 'fit-content',
              backgroundColor: '#1B76D2',
              color: '#FFFFFF',
              borderRadius: '8px',
            }}
          />
          
          {/* 位置 */}
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
            位置: 下から{ctaButton.bottomPercent || 0}%
          </Typography>
          
          {/* 横幅 */}
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
            横幅: {ctaButton.widthPercent || 0}%
          </Typography>
          
          {/* ホバー画像 */}
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
            ホバー画像: {ctaButton.hoverImage ? '設定済み' : '未設定'}
          </Typography>
          
          {/* URL */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <LinkIcon sx={{ fontSize: 16, color: '#666666' }} />
            <Typography 
              variant="body2"
              sx={{ 
                fontSize: '12px',
                color: '#666666',
                wordBreak: 'break-all',
                flex: 1,
              }}
            >
              {ctaButton.linkUrl || '未設定'}
            </Typography>
            {ctaButton.linkUrl && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyUrl(ctaButton.linkUrl!);
                }}
                sx={{ 
                  padding: '4px',
                  color: '#666666',
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

